// ---- Message contracts ----
export type FromWeb =
  | { type: "BRIDGE_PING"; id?: string }
  | { type: "REQUEST_PUSH_PERMS"; id?: string }
  | { type: "SHARE"; payload?: { text?: string; url?: string }; id?: string }
  | {
      type: "GOOGLE_SIGN_IN";
      payload?: { prompt?: "select_account" | "consent" };
      id?: string;
    }
  | {
      type: "APPLE_SIGN_IN";
      // keep payload optional in case you want options later
      payload?: { preferFullNameScope?: boolean };
      id?: string;
    }
  | { type: "PICK_FILE"; payload?: { types?: string[] }; id?: string }
  | {
      type: "PICK_DATETIME";
      // NEW: minimum lets native enforce "no past times"
      payload?: { initial?: string; minimum?: string };
      id?: string;
    }
  | { type: "OPEN_URL"; payload: { url: string }; id?: string }
  | { type: "RC_PURCHASE"; payload: { pkg: "monthly" | "yearly" }; id?: string }
  | { type: "RC_RESTORE"; id?: string }
  | { type: "RC_SYNC"; id?: string };

export type ToWeb =
  | { type: "BRIDGE_PONG"; payload: { version: string }; id?: string }
  | { type: "PUSH_TOKEN"; token: string | null; id?: string; error?: string }
  | { type: "SHARED"; ok: boolean; id?: string }
  | {
      type: "GOOGLE_SIGN_IN_RESULT";
      ok: boolean;
      idToken?: string;
      accessToken?: string;
      error?: string;
      id?: string;
    }
  | {
      type: "APPLE_SIGN_IN_RESULT";
      ok: boolean;
      idToken?: string;
      authorizationCode?: string;
      error?: string;
      id?: string;
    }
  | {
      type: "FILE_PICKED";
      ok: boolean;
      file?: { name?: string; uri: string; size?: number; mimeType?: string };
      id?: string;
    }
  | {
      type: "DATETIME_PICKED";
      ok: boolean;
      value?: string;
      id?: string;
    }
  // Foreground push received while app is open
  | { type: "PUSH_RECEIVED"; payload: any; id?: string }
  // Notification tap / cold start open
  | { type: "PUSH_OPENED"; payload: { data?: any }; id?: string }
  // Widget / deep link navigation
  | { type: "NAVIGATE"; payload: { route: "today" | "completed" | "new" }; id?: string }
  | {
      type: "WIDGET_SET_TODAY_SUMMARY";
      payload: { dateISO: string; done: number; total: number; updatedAt?: number };
      id?: string;
    }
  | { type: "WIDGET_SYNC_REQUEST"; payload?: { reason?: string }; id?: string }
  | {
      type: "RC_STATUS";
      ok: boolean;
      active: boolean;
      entitlementId: string;
      error?: string;
      id?: string;
    };

// ---- Env helpers ----
export function isNativeWebView() {
  return (
    typeof window !== "undefined" &&
    typeof (window as any).ReactNativeWebView !== "undefined" &&
    typeof (window as any).ReactNativeWebView.postMessage === "function"
  );
}

const RN = () => (window as any).ReactNativeWebView;

function randId() {
  return Math.random().toString(36).slice(2);
}

function parseMsg(data: any): ToWeb | null {
  try {
    if (typeof data === "string") return JSON.parse(data);
    if (typeof data === "object" && data) return data as ToWeb;
  } catch {}
  return null;
}

function toIsoSafe(v?: string): string | undefined {
  if (!v) return undefined;
  const t = Date.parse(v);
  if (Number.isNaN(t)) return undefined;
  return new Date(t).toISOString();
}

// ---- Posting to native ----
export function nativePost(msg: FromWeb) {
  if (!isNativeWebView()) return;
  try {
    RN().postMessage(JSON.stringify(msg));
  } catch {
    // no-op
  }
}

// ---- Subscribe to ALL native messages (with unsubscribe) ----
export function onNativeMessage(cb: (evt: ToWeb) => void) {
  const handler = (ev: any) => {
    const m = parseMsg(ev?.data);
    if (m && (m as any).type) cb(m);
  };

  window.addEventListener("message", handler);
  document.addEventListener("message", handler as any);

  return () => {
    window.removeEventListener("message", handler);
    document.removeEventListener("message", handler as any);
  };
}

/**
 * Wait for a correlated response type + id.
 * By default returns null on timeout (so your callers can "soft fail").
 */
function waitForResponse<T extends ToWeb["type"], R>(
  type: T,
  id: string,
  timeoutMs: number,
  map: (m: Extract<ToWeb, { type: T }>) => R | null
): Promise<R | null> {
  return new Promise((resolve) => {
    let done = false;

    const cleanup = (handler: any, timer?: any) => {
      if (done) return;
      done = true;
      window.removeEventListener("message", handler);
      document.removeEventListener("message", handler as any);
      if (timer) clearTimeout(timer);
    };

    const handler = (ev: any) => {
      const m = parseMsg(ev?.data) as any;
      if (!m || m.type !== type) return;
      if (m.id && m.id !== id) return;

      const out = map(m);
      if (out === null) return;

      cleanup(handler, timer);
      resolve(out);
    };

    window.addEventListener("message", handler);
    document.addEventListener("message", handler as any);

    const timer = setTimeout(() => {
      cleanup(handler);
      resolve(null);
    }, timeoutMs);

    window.addEventListener(
      "beforeunload",
      () => {
        cleanup(handler, timer);
      },
      { once: true }
    );
  });
}

// ---- Request push token with correlation + timeout ----
export function requestPushToken(opts?: { timeoutMs?: number }): Promise<string | null> {
  if (!isNativeWebView()) return Promise.resolve(null);

  const id = randId();
  const timeoutMs = opts?.timeoutMs ?? 10000;

  nativePost({ type: "REQUEST_PUSH_PERMS", id });

  return waitForResponse("PUSH_TOKEN", id, timeoutMs, (m) => (m.error ? null : m.token ?? null));
}

// ---- Google sign-in via native (token roundtrip) ----
export function requestGoogleTokens(opts?: { timeoutMs?: number }) {
  if (!isNativeWebView()) return Promise.resolve(null);

  const id = randId();
  const timeoutMs = opts?.timeoutMs ?? 20000;

  nativePost({
    type: "GOOGLE_SIGN_IN",
    payload: { prompt: "select_account" },
    id,
  });

  return waitForResponse(
    "GOOGLE_SIGN_IN_RESULT",
    id,
    timeoutMs,
    (m) => {
      if (!m.ok || !m.idToken) return null;
      return { idToken: m.idToken, accessToken: m.accessToken };
    }
  ) as Promise<{ idToken: string; accessToken?: string } | null>;
}

// ---- Apple sign-in via native (token roundtrip) ----
export function requestAppleTokens(opts?: { timeoutMs?: number }) {
  if (!isNativeWebView()) return Promise.resolve(null);

  const id = randId();
  const timeoutMs = opts?.timeoutMs ?? 20000;

  nativePost({
    type: "APPLE_SIGN_IN",
    id,
  });

  return waitForResponse(
    "APPLE_SIGN_IN_RESULT",
    id,
    timeoutMs,
    (m) => {
      if (!m.ok || !m.idToken) return null;
      return { idToken: m.idToken, authorizationCode: m.authorizationCode };
    }
  ) as Promise<{ idToken: string; authorizationCode?: string } | null>;
}

// ---- File picker with correlation ----
export function pickFile(types?: string[], opts?: { timeoutMs?: number }) {
  if (!isNativeWebView()) return Promise.resolve(null);

  const id = randId();
  const timeoutMs = opts?.timeoutMs ?? 15000;

  nativePost({ type: "PICK_FILE", payload: { types }, id });

  return waitForResponse(
    "FILE_PICKED",
    id,
    timeoutMs,
    (m) => (m.ok ? m.file ?? null : null)
  ) as Promise<{ name?: string; uri: string; size?: number; mimeType?: string } | null>;
}

/**
 * Opens the native datetime picker.
 * Enhancement: defaults to a minimum of "now" so past times are blocked.
 * You can override with opts.minimum (ISO string).
 */
export function pickDateTime(
  initial?: string,
  opts?: { timeoutMs?: number; minimum?: string }
): Promise<string | null> {
  if (!isNativeWebView()) return Promise.resolve(null);

  const id = randId();
  const timeoutMs = opts?.timeoutMs ?? 15000;

  const nowIso = new Date().toISOString();
  const minimumIso = toIsoSafe(opts?.minimum) ?? nowIso;

  let initialIso = toIsoSafe(initial) ?? minimumIso;
  if (Date.parse(initialIso) < Date.parse(minimumIso)) initialIso = minimumIso;

  nativePost({
    type: "PICK_DATETIME",
    payload: { initial: initialIso, minimum: minimumIso },
    id,
  });

  return waitForResponse(
    "DATETIME_PICKED",
    id,
    timeoutMs,
    (m) => (m.ok ? m.value ?? null : null)
  ) as Promise<string | null>;
}

// ---- Open URL (handy for external links) ----
export function openUrl(url: string) {
  if (!isNativeWebView()) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  nativePost({ type: "OPEN_URL", payload: { url }, id: randId() });
}

// ---- Share (fire-and-forget) ----
export async function share(payload: { text?: string; url?: string }) {
  if (!isNativeWebView()) return false;
  nativePost({ type: "SHARE", payload, id: randId() });
  return true;
}

// ---- RevenueCat purchase / restore / sync ----
export async function rcPurchase(pkg: "monthly" | "yearly", opts?: { timeoutMs?: number }) {
  if (!isNativeWebView()) return null;

  const id = randId();
  nativePost({ type: "RC_PURCHASE", payload: { pkg }, id });

  const timeoutMs = opts?.timeoutMs ?? 45000;

  return waitForResponse("RC_STATUS", id, timeoutMs, (m) => ({ 
    ok: m.ok, 
    active: m.active,
    error: m.error,
  }));
}

export async function rcRestore(opts?: { timeoutMs?: number }) {
  if (!isNativeWebView()) return null;

  const id = randId();
  nativePost({ type: "RC_RESTORE", id });

  const timeoutMs = opts?.timeoutMs ?? 45000;

  return waitForResponse("RC_STATUS", id, timeoutMs, (m) => ({ ok: m.ok, active: m.active }));
}

export async function rcSync(opts?: { timeoutMs?: number }) {
  if (!isNativeWebView()) return null;

  const id = randId();
  nativePost({ type: "RC_SYNC", id });

  const timeoutMs = opts?.timeoutMs ?? 15000;

  return waitForResponse("RC_STATUS", id, timeoutMs, (m) => ({ ok: m.ok, active: m.active }));
}
